import { useCallback, useContext, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { ApplicantInactivityTimeout } from '@aidkitorg/component-library';
import { PublicConfigurationContext } from '../Context';
import { usePost } from '../API';
import { getPortalToken, portalLogout, trySetPortalToken } from '../Hooks/PortalSession';
import { trySetLinkKey, tryGetLinkKey } from '../Util';

// Defaults when a program doesn't configure logoutInactiveApplicant.
const DEFAULT_LOG_OUT_AFTER_MINUTES = 30;
const DEFAULT_WARN_BEFORE_LOGOUT_MINUTES = 2;

// Activity fires often (debounced ~500ms); only actually re-mint at most once every 5 minutes.
const REFRESH_THROTTLE_MS = 5 * 60 * 1000;

// After a failed keepalive, allow a retry this soon instead of waiting the full
// throttle window — while still bounding retries during a persistent outage.
const REFRESH_RETRY_BACKOFF_MS = 30 * 1000;

// Dispatched on activity/stay-active so the portal home can slide the
// subsurvey-link tokens it holds in React state (which the keeper can't reach).
export const REFRESH_SESSION_EVENT = 'aidkit:refresh-session';

/**
 * Keeps an applicant's portal session alive while they're present and logs them
 * out after inactivity. Gated on the new applicant portal AND an actual portal
 * session, so legacy programs and admins/screeners (auth_token, no portal token)
 * are untouched.
 *
 * On activity it re-mints every expiring token in the current page context in
 * one batch: the `portal` cookie, plus — on a subsurvey page — that page's
 * `key:<path>` cookie (the token the fill authenticates with). The non-expiring
 * /apply tokens (sessionStorage `DynamoAppToken`/`status`) carry no expiry, so
 * they're left alone. It also broadcasts {@link REFRESH_SESSION_EVENT} so the
 * portal home can slide the subsurvey-link tokens it holds in React state. On
 * genuine idle it clears the session and redirects.
 */
export function ApplicantSessionKeeper() {
  const publicConfig = useContext(PublicConfigurationContext);
  const idleConfig = publicConfig?.applicantPortal?.logoutInactiveApplicant;
  const logOutAfterMinutes = idleConfig?.logOutAfterMinutes || DEFAULT_LOG_OUT_AFTER_MINUTES;
  const warnBeforeLogoutMinutes = idleConfig?.warnBeforeLogoutMinutes || DEFAULT_WARN_BEFORE_LOGOUT_MINUTES;
  // Slide the token well before it expires: cap at REFRESH_THROTTLE_MS but never more
  // than half the token lifetime (idle window + 2 min), so a short idle config can't
  // let an actively-using applicant's token lapse between throttled refreshes.
  const refreshThrottleMs = Math.min(REFRESH_THROTTLE_MS, ((logOutAfterMinutes + 2) * 60 * 1000) / 2);
  // Read/write the portal cookie and the current page's subsurvey `key:` cookie
  // via react-cookie, so refreshed values flow reactively to every consumer:
  // ApplicantPortalPage reads `cookies['portal']`, and the subsurvey page reads
  // the key via useLinkKey -> useToken. Writing the portal token with raw
  // document.cookie would NOT update those react-cookie reads (it would keep
  // sending the original login token until the fixed 1h expiry).
  const keyCookieName = encodeURIComponent('key:' + window.location.pathname);
  const [cookies, setCookie] = useCookies([keyCookieName, 'portal']);
  const refreshTokens = usePost('/applicant/refresh_token', {
    token: () => getPortalToken() || '',
    // Keepalive failures must not toast or redirect the applicant; we handle the
    // error response ourselves below.
    handleErrors: () => {},
  });
  const lastRefresh = useRef(0);

  // force=true bypasses the throttle (used when the applicant explicitly clicks
  // "stay logged in"); activity-driven refreshes are throttled.
  const refreshSession = useCallback(async (force: boolean) => {
    const now = Date.now();
    if (!force && now - lastRefresh.current < refreshThrottleMs) return;
    lastRefresh.current = now;
    let refreshed = false;
    try {
      // Send the current page's subsurvey key alongside the portal token; the
      // endpoint re-mints both (and no-ops anything non-expiring). Fall back to the
      // sessionStorage mirror when the cookie is gone (some in-app browsers clear
      // it), matching API.tsx's auth precedence — otherwise the surviving key never
      // gets slid.
      const keyToken = cookies[keyCookieName] || tryGetLinkKey(window.location.pathname);
      const res = await refreshTokens({ additionalTokens: keyToken ? [keyToken] : [] });
      if (res && 'token' in res) {
        refreshed = true;
        // setCookie (not raw document.cookie) so react-cookie consumers update;
        // also mirror to sessionStorage so the slid token survives a cookie wipe.
        if (res.token) {
          setCookie('portal', res.token, { path: '/' });
          trySetPortalToken(res.token);
        }
        const freshKey = keyToken ? res.refreshed?.[keyToken] : undefined;
        if (freshKey) {
          // Write through both path scopes the key cookie is set at (root + /p)
          // and the sessionStorage mirror, so useLinkKey/useToken pick it up.
          setCookie(keyCookieName, freshKey, { path: '/' });
          setCookie(keyCookieName, freshKey, { path: '/p' });
          trySetLinkKey(window.location.pathname, freshKey);
        }
      } else if (res && 'error' in res) {
        // The portal token can't be refreshed (expired/invalid) — the session is
        // over. End it cleanly via portalLogout rather than leaving subsequent
        // requests to fail; don't fall through to dispatch the refresh event.
        portalLogout(publicConfig);
        return;
      }
    } catch {
      // A failed keepalive shouldn't surface to the applicant; fall through to the
      // backoff below so activity can retry.
    }
    if (!refreshed) {
      // The keepalive threw or returned no fresh token (a transient failure, not an
      // expired session). Don't burn the whole throttle window — back the timestamp
      // off so the next activity retries shortly instead of being stranded.
      lastRefresh.current = now - refreshThrottleMs + REFRESH_RETRY_BACKOFF_MS;
    }
    window.dispatchEvent(new CustomEvent(REFRESH_SESSION_EVENT));
  }, [refreshTokens, cookies, keyCookieName, setCookie, refreshThrottleMs, publicConfig]);

  // Only run for an actual applicant portal session. Admins/screeners viewing
  // these routes carry an `auth_token`, not a `portal` token — they must not get
  // the applicant idle prompt, portalLogout, or the logoutInactiveApplicant config.
  if (!publicConfig?.applicantPortal || !getPortalToken()) return null;

  return (
    <ApplicantInactivityTimeout
      logOutAfterSeconds={logOutAfterMinutes * 60}
      warnBeforeLogoutSeconds={warnBeforeLogoutMinutes * 60}
      onLogout={() => portalLogout(publicConfig)}
      onAction={() => refreshSession(false)}
      onStillHere={() => refreshSession(true)}
    />
  );
}
